
import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { nutritionData } from '../data/nutritionData';
import { Search, AlertTriangle, Info } from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { getSealImage } from '../utils/sealUtils';

// Import all seal images
import SealSodio from '../images/Sodio.png';
import SealGordura from '../images/Gordura_Saturada.png';
import SealAcucar from '../images/Acucar_Adicionado.png';
import SealSodioGordura from '../images/Sodio_Gordura.png';
import SealAcucarGordura from '../images/Acucar_Gordura.png';
// Note: User provided 5 images. Missing "Acucar_Sodio" and "Todos" based on combinations.
// Based on the artifacts:
// g48q3ijp -> Sodio_Gordura.png
// elbuiox8 -> Gordura_Saturada.png
// ybvnrdeo -> Acucar_Gordura.png
// v9152xid -> Sodio.png
// 8abjai2u -> Acucar_Adicionado.png

const sealImageMap = {
    "Sodio.png": SealSodio,
    "Gordura_Saturada.png": SealGordura,
    "Acucar_Adicionado.png": SealAcucar,
    "Sodio_Gordura.png": SealSodioGordura,
    "Acucar_Gordura.png": SealAcucarGordura,
    // Fallbacks if specific combo missing, logic handles best fit or we might need more uploads
};

const WarningSeal = ({ nutrients }) => {
    const imageName = getSealImage(nutrients);
    const imageSrc = sealImageMap[imageName];

    if (!imageSrc) return null;

    return (
        <div className="mt-4">
             <img 
                src={imageSrc} 
                alt={`Alto em ${nutrients.join(', ')}`} 
                className="h-16 md:h-20 w-auto object-contain select-none" 
            />
        </div>
    )
}

const NutriRow = ({ label, data, isSub = false }) => (
    <div className={`flex justify-between border-b border-gray-300 pb-1 last:border-0 items-end ${isSub ? 'pl-4 text-gray-600' : ''}`}>
        <span className={`font-medium w-1/3 text-sm ${isSub ? 'font-normal' : 'text-gray-900'}`}>{label}</span>
        <div className="flex gap-2 w-2/3 justify-end text-right text-sm">
            <span className="w-1/3 text-gray-900 font-bold">{data.p100}</span>
            <span className="w-1/3 text-gray-900 font-bold">{data.portion}</span>
            <span className="w-[40px] text-gray-900 font-bold">{data.dv ? `${data.dv}%` : '**'}</span>
        </div>
    </div>
)

const NutritionalInfo = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredData = nutritionData.filter(item => 
    item.product.toLowerCase().includes(searchTerm.toLowerCase()) || 
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 pt-[164px]">
      <Header />
      
      <main className="pb-20">
         <div className="bg-[#E63312] text-white py-16">
            <div className="container mx-auto px-4 text-center">
                <h1 className="text-4xl md:text-5xl font-black uppercase tracking-wide mb-4">Tabela Nutricional</h1>
                <p className="text-lg opacity-90 max-w-2xl mx-auto">Confira os ingredientes, informações nutricionais e alergênicos de todos os nossos produtos.</p>
                
                <div className="max-w-md mx-auto mt-8 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input 
                        placeholder="Buscar produto..." 
                        className="pl-10 h-12 rounded-full text-gray-900 bg-white border-none shadow-xl"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>
         </div>

         <div className="container mx-auto px-4 py-12 max-w-4xl">
            <Accordion type="single" collapsible className="space-y-4">
                {filteredData.map((item) => (
                    <ProductAccordionItem key={item.id} item={item} />
                ))}
            </Accordion>
         </div>
      </main>

      <Footer />
    </div>
  );
};

const ProductAccordionItem = ({ item }) => {
    const [selectedPackage, setSelectedPackage] = useState(item.variations[0].package);
    
    const currentVariation = item.variations.find(v => v.package === selectedPackage) || item.variations[0];

    return (
        <AccordionItem value={item.id} className="border border-gray-200 rounded-xl px-4 shadow-sm bg-white overflow-hidden">
            <AccordionTrigger className="hover:no-underline py-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4 text-left w-full">
                    <div>
                        <h3 className="text-xl font-black text-[#E63312] uppercase">{item.product}</h3>
                        <span className="text-sm text-gray-500 font-medium">Selecione a embalagem para ver detalhes</span>
                    </div>
                    <Badge className="ml-auto mr-4 bg-[#FFC300] text-[#E63312] hover:bg-[#FFC300] hover:text-[#E63312] uppercase">{item.category}</Badge>
                </div>
            </AccordionTrigger>
            <AccordionContent className="pb-8">
                <div className="grid md:grid-cols-2 gap-12 pt-4">
                    
                    {/* Left Column: Table & Package Select */}
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700">Embalagem:</label>
                            <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                                <SelectTrigger className="w-full bg-gray-50 border-gray-300">
                                    <SelectValue placeholder="Selecione..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {item.variations.map((v) => (
                                        <SelectItem key={v.package} value={v.package}>{v.package}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Nutrition Table */}
                        <div className="border-2 border-black p-4 bg-white">
                            <h4 className="font-black text-xl mb-1">INFORMAÇÃO NUTRICIONAL</h4>
                            <div className="text-sm font-medium mb-4 space-y-0.5">
                                <p>Porções por embalagem: <span className="font-bold">{currentVariation.servings}</span></p>
                                <p>Porção: <span className="font-bold">{currentVariation.portionSize} ({currentVariation.homeMeasure})</span></p>
                            </div>
                            
                            <div className="space-y-1 text-sm">
                                <div className="flex justify-between font-bold border-b-2 border-black pb-1 mb-2">
                                    <span className="w-1/3"></span>
                                    <div className="flex gap-2 w-2/3 justify-end text-right">
                                        <span className="w-1/3">100 g</span>
                                        <span className="w-1/3">{currentVariation.portionSize}</span>
                                        <span className="w-[40px] text-right">%VD*</span>
                                    </div>
                                </div>
                                <NutriRow label="Valor energético (kcal)" data={currentVariation.table.energy} />
                                <NutriRow label="Carboidratos (g)" data={currentVariation.table.carbs} />
                                <NutriRow label="Açúcares totais (g)" data={currentVariation.table.totalSugars} isSub={true} />
                                <NutriRow label="Açúcares adic. (g)" data={currentVariation.table.addedSugars} isSub={true} />
                                <NutriRow label="Proteínas (g)" data={currentVariation.table.proteins} />
                                <NutriRow label="Gorduras totais (g)" data={currentVariation.table.totalFat} />
                                <NutriRow label="Gorduras saturadas (g)" data={currentVariation.table.saturatedFat} isSub={true} />
                                <NutriRow label="Gorduras trans (g)" data={currentVariation.table.transFat} isSub={true} />
                                <NutriRow label="Fibra alimentar (g)" data={currentVariation.table.fiber} />
                                <NutriRow label="Sódio (mg)" data={currentVariation.table.sodium} />
                            </div>
                            <p className="text-[10px] mt-4 leading-tight text-gray-600 font-medium border-t border-black pt-2">
                                * Percentual de valores diários fornecidos pela porção.
                            </p>
                        </div>
                    </div>

                    {/* Right Column: Info */}
                    <div className="space-y-8">
                        <div>
                            <h4 className="font-black text-[#E63312] flex items-center gap-2 mb-3 uppercase">
                                <Info className="w-5 h-5" /> Ingredientes
                            </h4>
                            <p className="text-gray-700 text-sm leading-relaxed border-l-4 border-[#FFC300] pl-4">
                                {item.ingredients}
                            </p>
                        </div>

                        <div>
                            <h4 className="font-black text-[#E63312] flex items-center gap-2 mb-3 uppercase">
                                <AlertTriangle className="w-5 h-5" /> Alergênicos
                            </h4>
                            <p className="text-gray-900 font-bold text-sm leading-relaxed bg-[#FFF8E3] p-4 rounded-lg border border-[#FFC300]">
                                {item.allergens}
                            </p>
                        </div>

                        <div className="pt-8 mt-auto border-t border-gray-100">
                                <p className="text-xs text-gray-400 font-medium mb-4">
                                Responsável Técnico(a): <br/>
                                <span className="text-gray-600 font-bold text-sm">{item.nutritionist}</span>
                                </p>
                                
                                {/* Seals - IMAGE BASED */}
                                {currentVariation.seals && currentVariation.seals.length > 0 && (
                                    <div className="flex justify-start">
                                        <WarningSeal nutrients={currentVariation.seals} />
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
            </AccordionContent>
        </AccordionItem>
    );
};

export default NutritionalInfo;
